import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/Index";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import CommentsTable from "./components/CommentsTable";
import HandwrittenComments from "./components/HandwrittenComments";
import BookingAndDeceasedDetails from "./components/BookingDeceasedDetails";
import TabButtons from "./components/TabButtons";
import { MemoryTableGallery } from "./components/MemoryTableGallery";
import { MemoryTableUpload } from "./components/MemoryTableUpload";
import { MemoryBookDelivery } from "./components/MemoryBookDelivery";
import { useLocation, useParams } from "react-router-dom";
import apiService from "../../../../service/Api";
import { showErrorToast } from "../../../../components/common/ShowToast";
import ContactsTab from "../../../bookings/view-booking2/tabs/memory-table/components/ContactsTab";
import { useMemoryMessages } from "../../../bookings/view-booking2/tabs/memory-table/hooks/useMemoryMessages";

export type activeTabType =
  | "tribute-comments"
  | "youtube-comments"
  | "handwritten-comments"
  | "contacts"
  | "media"
  | "upload"
  | "delivery";

type MemoryTableView2Props = {
  streamId?: string;
  hideHeaderDetails?: boolean;
  deceasedId?: number;
};

interface StreamVisitorRecord {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

interface StreamContactRecord {
  id: number;
  email: string | null;
}

const MemoryTableView2: React.FC<MemoryTableView2Props> = ({
  streamId,
  hideHeaderDetails = false,
}) => {
  const params = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const resolvedStreamId = useMemo(() => {
    return streamId ?? params.id ?? "";
  }, [streamId, params.id]);

  const { messages: fetchedMessages } = useMemoryMessages(
    resolvedStreamId ? Number(resolvedStreamId) : null,
  );

  const [streamVisitors, setStreamVisitors] = useState<StreamVisitorRecord[]>([]);
  const [streamContacts, setStreamContacts] = useState<StreamContactRecord[]>([]);

  useEffect(() => {
    if (!resolvedStreamId) {
      setStreamVisitors([]);
      setStreamContacts([]);
      return;
    }

    let cancelled = false;
    const fetchStreamContacts = async () => {
      try {
        const [visitorsResponse, contactsResponse] = await Promise.all([
          apiService.get<{ results: StreamVisitorRecord[] }>(
            `/api/streams/stream-visitors/?stream=${resolvedStreamId}&page_size=1000`,
          ),
          apiService.get<{ results: StreamContactRecord[] }>(
            `/api/contacts/contact/?from_stream=${resolvedStreamId}&page_size=1000`,
          ),
        ]);
        if (cancelled) return;
        setStreamVisitors(visitorsResponse.data.results ?? []);
        setStreamContacts(contactsResponse.data.results ?? []);
      } catch {
        if (!cancelled) {
          setStreamVisitors([]);
          setStreamContacts([]);
        }
      }
    };

    void fetchStreamContacts();
    return () => {
      cancelled = true;
    };
  }, [resolvedStreamId]);

  const contactsCount = useMemo(() => {
    const seen = new Set<string>();
    fetchedMessages.forEach((msg) => {
      const key = msg.sender_email?.trim().toLowerCase() || `no-email-${msg.id}`;
      seen.add(key);
    });
    streamVisitors.forEach((visitor) => {
      seen.add(visitor.email.trim().toLowerCase());
    });
    streamContacts.forEach((contact) => {
      seen.add(
        contact.email?.trim().toLowerCase() || `no-email-contact-${contact.id}`,
      );
    });
    return seen.size;
  }, [fetchedMessages, streamVisitors, streamContacts]);

  const [activeTab, setActiveTab] = useState<activeTabType>("tribute-comments");
  const [tributeCommentsCount, setTributeCommentsCount] = useState<number>(0);
  const [youtubeCommentsCount, setYoutubeCommentsCount] = useState<number>(0);
  const [handwrittenCommentsCount, setHandwrittenCommentsCount] = useState<number>(0);
  const [unapprovedMediaCount, setUnapprovedMediaCount] = useState<number>(0);

  const fetchApprovedComments = useCallback(async () => {
    if (!resolvedStreamId) return;

    try {
      const [generic, youtube] = await Promise.all([
        apiService.get<{ count: number }>(
          `/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=generic&is_spam=false&page_size=1`,
        ),
        apiService.get<{ count: number }>(
          `/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=youtube&is_spam=false&page_size=1`,
        ),
      ]);
      setTributeCommentsCount(generic.data.count);
      setYoutubeCommentsCount(youtube.data.count);
    } catch (error) {
      console.error("Failed to fetch comment counts:", error);
      showErrorToast("Failed to fetch comment counts");
    }
  }, [resolvedStreamId]);

  const fetchHandwrittenCount = useCallback(async () => {
  if (!resolvedStreamId) return;
    try {
      const res = await apiService.get<{ count: number }>(
        `/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=handwritten&page_size=1`
      );
      setHandwrittenCommentsCount(res.data.count);
    } catch (error) {
      console.error("Failed to fetch handwritten comments count:", error);
    }
  }, [resolvedStreamId]);
  
 const fetchMediaCount = useCallback(async () => {
  if (!resolvedStreamId) return;

  try {
    const res = await apiService.get<{ count: number }>(
      `/api/individuals/deceased-gallery/?stream_id=${resolvedStreamId}&status=unapproved&page_size=1`
    );
    setUnapprovedMediaCount(res.data.count);
  } catch (error) {
    console.error("Failed to fetch unapproved media count:", error);
  }
}, [resolvedStreamId]);

  const location = useLocation();
  const nameOfDeceased = location.state?.nameOfDeceased || "";

  useEffect(() => {
    fetchApprovedComments();
    fetchMediaCount();
    fetchHandwrittenCount();
  }, [fetchApprovedComments, fetchMediaCount]);

  if (!resolvedStreamId) {
    return <Box sx={{ p: 2 }}>Stream ID not found.</Box>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      {!hideHeaderDetails && (
        <BookingAndDeceasedDetails streamId={resolvedStreamId} />
      )}

      {/* Tab Nav */}
      <TabButtons
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tributeCommentsCount={tributeCommentsCount}
        youtubeCommentsCount={youtubeCommentsCount}
        contactsCount={contactsCount}
        mediaCount={unapprovedMediaCount}
        handwrittenCommentsCount={handwrittenCommentsCount}
      />

      {/* Active Tabs */}
      <Box component={"section"} sx={{ mt: 1 }}>
        {activeTab === "tribute-comments" && (
          <Box
            sx={{
              backgroundColor: reduxTheme.primary_colors[900],
              p: 1,
              mt: 2,
              borderRadius: 3,
            }}
          >
            <CommentsTable
              commentsType={"tribute comments"}
              endpoint={`/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=generic`}
              fetchApprovedComments={fetchApprovedComments}
              nameOfDeceased={nameOfDeceased}
            />
          </Box>
        )}

        {activeTab === "youtube-comments" && (
          <Box
            sx={{
              backgroundColor: reduxTheme.primary_colors[900],
              p: 1,
              mt: 2,
              borderRadius: 3,
            }}
          >
            <CommentsTable
              commentsType={"youtube comments"}
              endpoint={`/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=youtube`}
              fetchApprovedComments={fetchApprovedComments}
              resolvedStreamId={Number(resolvedStreamId)}
              nameOfDeceased={nameOfDeceased}
            />
          </Box>
        )}

        {activeTab === "handwritten-comments" && (
          <Box
            sx={{
              backgroundColor: reduxTheme.primary_colors[900],
              p: 1,
              mt: 2,
              borderRadius: 3,
            }}
          >
            <HandwrittenComments
              streamId={resolvedStreamId}
              onRefresh={fetchHandwrittenCount}
            />
          </Box>
        )}

        {activeTab === "contacts" && (
          <Box
            sx={{
              backgroundColor: reduxTheme.primary_colors[900],
              p: 1,
              mt: 2,
              borderRadius: 3,
            }}
          >
            <ContactsTab
              messages={fetchedMessages}
              streamId={resolvedStreamId ? Number(resolvedStreamId) : null}
              visitors={streamVisitors}
            />
          </Box>
        )}

        {activeTab === "media" && (
          <MemoryTableGallery onRefreshCount={fetchMediaCount} />
        )}
        {activeTab === "upload" && <MemoryTableUpload />}
        {activeTab === "delivery" && (
          <MemoryBookDelivery setActiveTab={setActiveTab} />
        )}
      </Box>
    </motion.div>
  );
};

export default MemoryTableView2;
